import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useTranslation } from 'react-i18next';
import { Lock, Globe, Bell, Loader2, Eye, EyeOff, AlertTriangle, Trash2, Link as LinkIcon } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const LANGUAGES = [
  { code: 'fr', label: 'Français' },
  { code: 'en', label: 'English' },
  { code: 'de', label: 'Deutsch' },
];

const NOTIF_KEYS = [
  { key: 'email_messages', label: 'Emails pour nouveaux messages' },
  { key: 'email_bookings', label: 'Emails pour réservations' },
  { key: 'email_community', label: 'Emails pour activité communautaire' },
  { key: 'push_messages', label: 'Notifications push · messages' },
  { key: 'push_bookings', label: 'Notifications push · réservations' },
  { key: 'push_community', label: 'Notifications push · communautés' },
];

const Settings = () => {
  const { i18n } = useTranslation();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [language, setLanguage] = useState('fr');
  const [prefs, setPrefs] = useState({});
  const [loading, setLoading] = useState(true);
  const [savingPrefs, setSavingPrefs] = useState(false);

  const [current, setCurrent] = useState('');
  const [next, setNext] = useState('');
  const [confirm, setConfirm] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNext, setShowNext] = useState(false);
  const [savingPwd, setSavingPwd] = useState(false);

  // RGPD — Account deletion
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleting, setDeleting] = useState(false);

  // Brief Prospection Phase 1 — handle public
  const [handleValue, setHandleValue] = useState('');
  const [savingHandle, setSavingHandle] = useState(false);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { withCredentials: true })
      .then(({ data }) => setHandleValue(data.handle || ''))
      .catch(() => {});
  }, []);

  const saveHandle = async () => {
    setSavingHandle(true);
    try {
      const { data } = await axios.patch(`${API}/users/me/handle`, { handle: handleValue }, { withCredentials: true });
      setHandleValue(data.handle);
      toast.success('Lien public mis à jour !');
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Impossible de mettre à jour le handle');
    } finally {
      setSavingHandle(false);
    }
  };

  const copyPublicLink = async () => {
    try {
      await navigator.clipboard.writeText(`${window.location.origin}/api/@${handleValue}`);
      toast.success('Lien de partage copié !');
    } catch {
      toast.error('Impossible de copier');
    }
  };

  useEffect(() => {
    const load = async () => {
      try {
        const { data } = await axios.get(`${API}/settings/preferences`, { withCredentials: true });
        setLanguage(data.language || 'fr');
        setPrefs(data.notification_preferences || {});
      } catch {
        toast.error('Impossible de charger les préférences');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const changePassword = async (e) => {
    e.preventDefault();
    if (next.length < 8) return toast.error('Le mot de passe doit contenir au moins 8 caractères');
    if (next !== confirm) return toast.error('Les mots de passe ne correspondent pas');
    setSavingPwd(true);
    try {
      await axios.put(`${API}/settings/password`, { current_password: current, new_password: next }, { withCredentials: true });
      toast.success('Mot de passe mis à jour');
      setCurrent(''); setNext(''); setConfirm('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Impossible de modifier le mot de passe');
    } finally {
      setSavingPwd(false);
    }
  };

  const saveLanguage = async (code) => {
    setLanguage(code);
    i18n.changeLanguage(code);
    try {
      await axios.put(`${API}/settings/preferences`, { language: code }, { withCredentials: true });
      toast.success('Langue mise à jour');
    } catch {
      toast.error('Sauvegarde impossible');
    }
  };

  const togglePref = async (key) => {
    const updated = { ...prefs, [key]: !prefs[key] };
    setPrefs(updated);
    setSavingPrefs(true);
    try {
      await axios.put(`${API}/settings/preferences`, { notification_preferences: updated }, { withCredentials: true });
    } catch {
      toast.error('Sauvegarde impossible');
      setPrefs(prefs); // rollback
    } finally {
      setSavingPrefs(false);
    }
  };

  // RGPD Article 17 — delete account permanently
  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'SUPPRIMER') {
      toast.error('Veuillez taper SUPPRIMER exactement pour confirmer.');
      return;
    }
    setDeleting(true);
    try {
      await axios.delete(`${API}/auth/me`, {
        data: { confirmation: 'SUPPRIMER' },
        withCredentials: true,
      });
      toast.success('Votre compte a été supprimé. À bientôt.');
      // Best-effort logout call (cookie is already cleared backend-side)
      try { await logout(); } catch { /* ignored — already deleted */ }
      navigate('/', { replace: true });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'La suppression a échoué. Réessayez ou contactez le support.');
    } finally {
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20 text-gray-500">
        <Loader2 className="h-6 w-6 animate-spin mr-2" /> Chargement...
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      <div>
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Paramètres</h1>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">Gérez votre compte, vos préférences et vos notifications.</p>
      </div>

      {/* Password */}
      <section data-testid="settings-password-section" className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70">
        <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Lock className="h-4 w-4" /> Changer le mot de passe</h2>
        <form onSubmit={changePassword} className="mt-4 space-y-3 max-w-md">
          <div className="relative">
            <input
              data-testid="settings-current-password"
              type={showCurrent ? 'text' : 'password'}
              placeholder="Mot de passe actuel"
              value={current}
              onChange={(e) => setCurrent(e.target.value)}
              className="w-full px-4 py-2.5 pr-10 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-sm"
              required
            />
            <button type="button" onClick={() => setShowCurrent(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {showCurrent ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <div className="relative">
            <input
              data-testid="settings-new-password"
              type={showNext ? 'text' : 'password'}
              placeholder="Nouveau mot de passe (8+ caractères)"
              value={next}
              onChange={(e) => setNext(e.target.value)}
              className="w-full px-4 py-2.5 pr-10 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-sm"
              required
              minLength={8}
            />
            <button type="button" onClick={() => setShowNext(s => !s)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400">
              {showNext ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <input
            data-testid="settings-confirm-password"
            type={showNext ? 'text' : 'password'}
            placeholder="Confirmer le nouveau mot de passe"
            value={confirm}
            onChange={(e) => setConfirm(e.target.value)}
            className="w-full px-4 py-2.5 border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 text-sm"
            required
            minLength={8}
          />
          <button
            data-testid="settings-save-password-btn"
            type="submit"
            disabled={savingPwd}
            className="px-5 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 text-white text-sm font-semibold rounded-xl disabled:opacity-60"
          >
            {savingPwd ? 'Mise à jour...' : 'Mettre à jour'}
          </button>
        </form>
      </section>

      {/* Language */}
      <section data-testid="settings-language-section" className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70">
        <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Globe className="h-4 w-4" /> Langue</h2>
        <div className="mt-4 flex flex-wrap gap-2">
          {LANGUAGES.map(l => (
            <button
              key={l.code}
              data-testid={`settings-language-${l.code}`}
              onClick={() => saveLanguage(l.code)}
              className={`px-4 py-2 rounded-xl text-sm font-medium border transition-all ${
                language === l.code
                  ? 'bg-purple-600 border-purple-600 text-white'
                  : 'bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 text-gray-700 dark:text-gray-300 hover:border-purple-400'
              }`}
            >{l.label}</button>
          ))}
        </div>
      </section>

      {/* Brief Prospection Phase 1 — lien public /@handle */}
      <section data-testid="settings-handle-section" className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70">
        <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><LinkIcon className="h-4 w-4" /> Mon lien public</h2>
        <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
          Votre page publique partageable sur LinkedIn, WhatsApp et ailleurs.
        </p>
        <div className="mt-4 flex flex-wrap items-center gap-2 max-w-md">
          <div className="flex items-center flex-1 min-w-[220px] border border-gray-200 dark:border-gray-700 rounded-xl bg-white dark:bg-gray-900 overflow-hidden">
            <span className="px-3 text-sm text-gray-400 select-none">@</span>
            <input
              data-testid="settings-handle-input"
              type="text"
              value={handleValue}
              onChange={(e) => setHandleValue(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, ''))}
              placeholder="votre-nom"
              className="flex-1 py-2.5 pr-3 bg-transparent text-sm focus:outline-none"
            />
          </div>
          <button
            data-testid="settings-handle-save-btn"
            onClick={saveHandle}
            disabled={savingHandle || !handleValue}
            className="px-4 py-2.5 text-white text-sm font-semibold rounded-xl disabled:opacity-60"
            style={{ backgroundColor: '#7F77DD' }}
          >
            {savingHandle ? 'Enregistrement…' : 'Enregistrer'}
          </button>
        </div>
        {handleValue && (
          <div className="mt-3 flex items-center gap-2 text-sm">
            <a
              href={`/@${handleValue}`}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[#7F77DD] font-medium"
              data-testid="settings-handle-preview"
            >
              {window.location.origin}/@{handleValue}
            </a>
            <button
              onClick={copyPublicLink}
              className="px-2.5 py-1 text-xs rounded-lg border border-gray-200 dark:border-gray-700 text-gray-500 hover:text-gray-700"
              data-testid="settings-handle-copy"
            >
              Copier le lien de partage
            </button>
          </div>
        )}
      </section>

      {/* Notifications */}
      <section data-testid="settings-notifications-section" className="p-5 rounded-2xl bg-white dark:bg-gray-800 border border-gray-200/70 dark:border-gray-700/70">
        <h2 className="font-semibold text-gray-900 dark:text-white flex items-center gap-2"><Bell className="h-4 w-4" /> Notifications</h2>
        <div className="mt-4 divide-y divide-gray-100 dark:divide-gray-700">
          {NOTIF_KEYS.map(({ key, label }) => (
            <label key={key} className="flex items-center justify-between py-3 cursor-pointer">
              <span className="text-sm text-gray-700 dark:text-gray-300">{label}</span>
              <button
                data-testid={`settings-notif-${key}`}
                type="button"
                onClick={() => togglePref(key)}
                className={`relative w-11 h-6 rounded-full transition-all ${prefs[key] ? 'bg-purple-600' : 'bg-gray-200 dark:bg-gray-700'}`}
                aria-pressed={!!prefs[key]}
              >
                <span className={`absolute top-0.5 ${prefs[key] ? 'left-5' : 'left-0.5'} w-5 h-5 bg-white rounded-full shadow transition-all`} />
              </button>
            </label>
          ))}
        </div>
        {savingPrefs && <p className="text-xs text-gray-400 mt-2">Sauvegarde...</p>}
      </section>

      {/* Danger Zone — RGPD Article 17 */}
      <section
        data-testid="settings-danger-zone"
        className="p-5 rounded-2xl bg-red-50 dark:bg-red-950/20 border border-red-200 dark:border-red-900/40"
      >
        <h2 className="font-semibold text-red-700 dark:text-red-300 flex items-center gap-2">
          <AlertTriangle className="h-4 w-4" /> Avant de partir…
        </h2>
        <div className="text-sm text-red-700/85 dark:text-red-300/85 mt-3 space-y-2">
          <p>
            On est triste à l'idée que vous partiez. Si quelque chose ne va pas, est-ce qu'on peut
            vous aider ? Écrivez-nous à
            {' '}
            <a
              href="mailto:info@opensapiens.ch?subject=Avant%20de%20partir"
              className="font-medium underline hover:text-red-800 dark:hover:text-red-200"
              data-testid="settings-support-mail-link"
            >
              info@opensapiens.ch
            </a>
            {' '}— on lit chaque message personnellement.
          </p>
          <p>
            Si vous êtes sûr·e de votre choix, la suppression de votre compte est{' '}
            <strong>définitive et irréversible</strong>. Votre profil, vos publications, vos sessions
            et vos messages personnels seront effacés immédiatement. Pour respecter l'historique des
            autres personnes avec qui vous avez interagi, vos réservations passées seront anonymisées
            plutôt que supprimées.
          </p>
        </div>
        <button
          data-testid="settings-delete-account-btn"
          type="button"
          onClick={() => { setDeleteConfirmText(''); setShowDeleteModal(true); }}
          className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 transition-colors"
        >
          <Trash2 className="h-4 w-4" />
          Je veux quand même supprimer mon compte
        </button>
      </section>

      {/* Delete confirmation modal */}
      {showDeleteModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="delete-account-modal">
          <div
            className="absolute inset-0 bg-black/60 backdrop-blur-sm"
            onClick={() => !deleting && setShowDeleteModal(false)}
          />
          <div className="relative bg-white dark:bg-gray-900 rounded-2xl shadow-2xl w-full max-w-md p-6 border border-red-200 dark:border-red-900/40">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 dark:bg-red-900/40 flex items-center justify-center">
                <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-300" />
              </div>
              <h3 className="text-lg font-bold text-gray-900 dark:text-white">Êtes-vous sûr·e ?</h3>
            </div>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
              Cette action est <strong>irréversible</strong>. Une dernière chose avant : avez-vous
              essayé de nous écrire à{' '}
              <a
                href="mailto:info@opensapiens.ch?subject=Avant%20de%20partir"
                className="text-red-600 dark:text-red-300 underline font-medium"
              >
                info@opensapiens.ch
              </a>
              {' '}? On peut peut-être résoudre ce qui vous gêne.
            </p>
            <p className="text-sm text-gray-600 dark:text-gray-300 mb-2">
              Si oui — pour confirmer, tapez <strong className="font-mono bg-red-50 dark:bg-red-950/40 px-1.5 py-0.5 rounded text-red-700 dark:text-red-300">SUPPRIMER</strong> ci-dessous :
            </p>
            <input
              data-testid="delete-confirm-input"
              type="text"
              autoComplete="off"
              value={deleteConfirmText}
              onChange={(e) => setDeleteConfirmText(e.target.value)}
              placeholder="SUPPRIMER"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-700 rounded-lg bg-white dark:bg-gray-800 text-sm focus:ring-2 focus:ring-red-300"
              disabled={deleting}
            />
            <div className="flex gap-3 mt-5">
              <button
                data-testid="delete-cancel-btn"
                type="button"
                onClick={() => setShowDeleteModal(false)}
                disabled={deleting}
                className="flex-1 px-4 py-2 rounded-xl border border-gray-300 dark:border-gray-700 text-sm font-medium text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 disabled:opacity-60"
              >
                Annuler
              </button>
              <button
                data-testid="delete-confirm-btn"
                type="button"
                onClick={handleDeleteAccount}
                disabled={deleting || deleteConfirmText !== 'SUPPRIMER'}
                className="flex-1 px-4 py-2 rounded-xl bg-red-600 text-white text-sm font-semibold hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-2"
              >
                {deleting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                {deleting ? 'Suppression…' : 'Supprimer définitivement'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
